import { useActiveSeason, useSeasonLeaderboard } from "@/hooks/useSeason";
import { Sparkles, Loader2, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

export function SeasonPassPanel() {
  const { season, loading } = useActiveSeason();
  const { rows, loading: lbLoading } = useSeasonLeaderboard(10);

  if (loading) {
    return (
      <div className="glass-card p-6 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }
  if (!season) return null;

  const pct = Math.min(100, Math.round((season.my_pass_points / season.pass_max_points) * 100));

  return (
    <div className="glass-card p-5 space-y-4 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="flex items-start gap-3 relative">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-primary flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-primary-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-accent font-semibold">Active Season</p>
          <h3 className="font-heading font-bold text-foreground">{season.title}</h3>
          <p className="text-xs text-muted-foreground truncate">{season.theme}</p>
        </div>
        <div className="text-right shrink-0 text-xs text-muted-foreground">
          <p>Ends</p>
          <p className="font-semibold text-foreground">{formatDistanceToNow(new Date(season.ends_at))}</p>
        </div>
      </div>

      <div className="space-y-1.5 relative">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Season Pass</span>
          <span className="font-heading font-bold text-foreground">{season.my_pass_points} / {season.pass_max_points}</span>
        </div>
        <div className="h-2.5 rounded-full bg-muted/30 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-accent to-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-xs relative">
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Rank</p>
          <p className="font-heading font-bold text-foreground">{season.my_rank ?? "—"}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Wins</p>
          <p className="font-heading font-bold text-foreground">{season.my_challenges_won}</p>
        </div>
        <div className="p-2 rounded-lg bg-muted/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Players</p>
          <p className="font-heading font-bold text-foreground">{season.total_participants}</p>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="space-y-1 pt-2 border-t border-border/50 relative">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Trophy className="w-3 h-3" /> Top Performers</p>
          {rows.slice(0, 5).map(r => (
            <div key={r.user_id} className={cn(
              "flex items-center gap-2 p-1.5 rounded text-xs",
              r.is_self && "bg-primary/10"
            )}>
              <span className="w-5 text-muted-foreground font-bold text-center">{r.rank}</span>
              <p className="flex-1 truncate text-foreground">{r.display_name || "Anonymous"}{r.is_self && <span className="text-primary ml-1">(you)</span>}</p>
              <span className="font-heading font-bold text-foreground tabular-nums">{r.pass_points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
